import './style/Signup.css';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import RatingItem from '../components/RatingItem';
import { supabase } from '../supabaseClient';

const Signup = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [firstNameError, setFirstNameError] = useState('');
    const [lastNameError, setLastNameError] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [firstNameTouched, setFirstNameTouched] = useState(false);
    const [lastNameTouched, setLastNameTouched] = useState(false);
    const [usernameTouched, setUsernameTouched] = useState(false);
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);
    const ratingDescriptions = [
        'Needs foundation (200–369)',
        'Emerging (370–419)',
        'Developing (420–469)',
        'On track (470–549)',
        'Strong (550–609)',
        'Advanced (610–679)',
        'Excellent (680–800)'
    ];
    const [algebraRating, setAlgebraRating] = useState(200);
    const [algebraClass, setAlgebraClass] = useState(['selected', '', '', '', '', '', '']);
    const [algebraRatingDescription, setAlgebraRatingDescription] = useState(ratingDescriptions[0]);
    const [advancedMathRating, setAdvancedMathRating] = useState(200);
    const [advancedMathClass, setAdvancedMathClass] = useState(['selected', '', '', '', '', '', '']);
    const [advancedMathRatingDescription, setAdvancedMathRatingDescription] = useState(ratingDescriptions[0]);
    const [problemSolvingAndDataAnalysisRating, setProblemSolvingAndDataAnalysisRating] = useState(200);
    const [problemSolvingAndDataAnalysisClass, setProblemSolvingAndDataAnalysisClass] = useState(['selected', '', '', '', '', '', '']);
    const [problemSolvingAndDataAnalysisRatingDescription, setProblemSolvingAndDataAnalysisRatingDescription] = useState(ratingDescriptions[0]);
    const [geometryAndTrigonometryRating, setGeometryAndTrigonometryRating] = useState(200);
    const [geometryAndTrigonometryClass, setGeometryAndTrigonometryClass] = useState(['selected', '', '', '', '', '', '']);
    const [geometryAndTrigonometryRatingDescription, setGeometryAndTrigonometryRatingDescription] = useState(ratingDescriptions[0]);
    const [signupError, setSignupError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [signupSuccess, setSignupSuccess] = useState(false);
    const nameRegex = /^[A-Za-z]+$/;
    const usernameRegex = /^[A-Za-z0-9_]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const validateFirstName = (value, touched = false) => {
        if (value.length === 0) return touched ? 'Required' : '';
        if (value.length < 2) return 'At least 2 characters';
        if (value.length > 30) return 'Max 30 characters';
        if (!nameRegex.test(value)) return 'Letters only';
        return '';
    };

    const validateLastName = (value, touched = false) => {
        if (value.length === 0) return touched ? 'Required' : '';
        if (value.length < 2) return 'At least 2 characters';
        if (value.length > 30) return 'Max 30 characters';
        if (!nameRegex.test(value)) return 'Letters only';
        return '';
    };

    const validateUsername = (value, touched = false) => {
        if (value.length === 0) return touched ? 'Required' : '';
        if (value.length < 3) return 'At least 3 characters';
        if (value.length > 20) return 'Max 20 characters';
        if (!usernameRegex.test(value)) return 'Letters, numbers, and underscores only';
        return '';
    };

    const validatePassword = (value, touched = false) => {
        if (value.length === 0) return touched ? 'Required' : '';
        if (value.length < 8) return 'At least 8 characters';
        if (!/[A-Z]/.test(value)) return 'Include an uppercase letter';
        if (!/[a-z]/.test(value)) return 'Include a lowercase letter';
        if (!/[0-9]/.test(value)) return 'Include a number';
        if (!/[^A-Za-z0-9]/.test(value)) return 'Include a special character';
        return '';
    };

    const validateEmail = (value, touched = false) => {
        if (value.length === 0) return touched ? 'Required' : '';
        if (!emailRegex.test(value)) return 'Enter a valid email';
        return '';
    };

    const handleFirstNameChange = (e) => {
        const val = e.target.value;
        setFirstName(val);
        if (firstNameTouched) setFirstNameError(validateFirstName(val));
    };

    const handleLastNameChange = (e) => {
        const val = e.target.value;
        setLastName(val);
        if (lastNameTouched) setLastNameError(validateLastName(val, true));
    };

    const handleUsernameChange = (e) => {
        const val = e.target.value;
        setUsername(val);
        const err = validateUsername(val, true);
        if (usernameTouched) setUsernameError(err);
    };

    const handlePasswordChange = (e) => {
        const val = e.target.value;
        setPassword(val);
        if (passwordTouched) setPasswordError(validatePassword(val, true));
    };

    const handleEmailChange = (e) => {
        const val = e.target.value;
        setEmail(val);
        const err = validateEmail(val, true);
        if (emailTouched) setEmailError(err);
    };

    const handleFirstNameBlur = () => { 
        setFirstNameTouched(true); 
        setFirstNameError(validateFirstName(firstName, true)); 
    };

    const handleLastNameBlur = () => { 
        setLastNameTouched(true); 
        setLastNameError(validateLastName(lastName, true)); 
    };

    const handleUsernameBlur = () => {
        setUsernameTouched(true);
        const err = validateUsername(username, true);
        setUsernameError(err);
    };

    const handlePasswordBlur = () => { 
        setPasswordTouched(true); 
        setPasswordError(validatePassword(password, true)); 
    };
    
    const handleEmailBlur = () => {
        setEmailTouched(true);
        const err = validateEmail(email, true);
        setEmailError(err);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setFirstNameTouched(true); 
        setLastNameTouched(true); 
        setUsernameTouched(true); 
        setPasswordTouched(true); 
        setEmailTouched(true);
        const fnErr = validateFirstName(firstName, true);
        const lnErr = validateLastName(lastName, true);
        const unErr = validateUsername(username, true);
        const pwErr = validatePassword(password, true);
        const emErr = validateEmail(email, true);
        setFirstNameError(fnErr); 
        setLastNameError(lnErr); 
        setUsernameError(unErr); 
        setPasswordError(pwErr); 
        setEmailError(emErr);
        if (fnErr || lnErr || unErr || pwErr || emErr) return;
        setLoading(true);
        setSignupError(false);
        try {
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: { username, first_name: firstName, last_name: lastName },
                },
            });
            if (error) {
                setSignupError(true);
                setErrorMessage(error.message);
                return;
            }
            if (data.user && data.user.identities && data.user.identities.length === 0) {
                setSignupError(true);
                setErrorMessage('An account with this email already exists.');
                return;
            }
            const profileRes = await fetch(`${process.env.REACT_APP_API_BASE}/signup/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    authId: data.user.id,
                    username, firstName, lastName,
                    algebraRating, advancedMathRating,
                    problemSolvingAndDataAnalysisRating, geometryAndTrigonometryRating,
                }),
            });
            const profileData = await profileRes.json();
            if (!profileData.success) {
                setSignupError(true);
                setErrorMessage(profileData.message);
                return;
            }
            setSignupSuccess(true);
        } 
        catch (error) {
            console.error('Error:', error);
            setSignupError(true);
            setErrorMessage('Something went wrong. Please try again.');
        }
        finally {
            setLoading(false);
        }
    }

    const handleRatingClick = (index, setClass, setRating, setDescription) => {
        setClass(prev => prev.map((_, i) => i <= index ? 'selected' : ''));
        setRating([200, 370, 420, 470, 550, 610, 680][index]);
        setDescription(ratingDescriptions[index]);
    };

    const handleRatingHover = (index, setDescription) => {
        setDescription(ratingDescriptions[index]);
    };

    const handleRatingLeave = (rating, setDescription) => {
        const idx = [200, 370, 420, 470, 550, 610, 680].indexOf(rating);
        setDescription(idx !== -1 ? ratingDescriptions[idx] : '');
    };

    return (
        <div className="signup-container">
            <PublicNav />
            {signupSuccess ? (
                <div className='signup-success'>
                    <div className='panel-header'>
                        <h3>Check Your Email</h3>
                        <p>We sent a verification link to <strong>{email}</strong></p>
                    </div>
                    <p className='success-detail'>Click the link in the email to activate your account, then come back to log in.</p>
                    <Link to="/login" className='back-to-login'>Go to Login</Link>
                </div>
            ) : (
            <form onSubmit={onSubmit}>
                    <div className='panel-header'>
                        <h3>Create Account</h3>
                        <p>Set up your profile and skill ratings</p>
                    </div>
                    <div className='inputs-section'>
                        <div className='personal-info-fields'>
                            <div className='name-row'>
                                <div className={`input-group${firstNameError && firstNameTouched ? ' input-error' : ''}`}>
                                    <input className='first-name-input' type='text' name='firstName' id='firstName' value={firstName} onChange={handleFirstNameChange} onBlur={handleFirstNameBlur} placeholder=' '/>
                                    <label htmlFor='firstName'>First Name</label>
                                    {firstNameError && firstNameTouched && <span className='field-error'>{firstNameError}</span>}
                                </div>
                                <div className={`input-group${lastNameError && lastNameTouched ? ' input-error' : ''}`}>
                                    <input className='last-name-input' type='text' name='lastName' id='lastName' value={lastName} onChange={handleLastNameChange} onBlur={handleLastNameBlur} placeholder=' '/>
                                    <label htmlFor='lastName'>Last Name</label>
                                    {lastNameError && lastNameTouched && <span className='field-error'>{lastNameError}</span>}
                                </div>
                            </div>
                            <div className={`input-group${usernameError && usernameTouched ? ' input-error' : ''}`}>
                                <input className='username-input' type='text' name='username' id='username' value={username} onChange={handleUsernameChange} onBlur={handleUsernameBlur} placeholder=' '/>
                                <label htmlFor='username'>Username</label>
                                {usernameError && usernameTouched && <span className='field-error'>{usernameError}</span>}
                            </div>
                            <div className={`input-group${passwordError && passwordTouched ? ' input-error' : ''}`}>
                                <input className='password-input' type='password' name='password' id='password' value={password} onChange={handlePasswordChange} onBlur={handlePasswordBlur} placeholder=' '/>
                                <label htmlFor='password'>Password</label>
                                {passwordError && passwordTouched && <span className='field-error'>{passwordError}</span>}
                            </div>
                            <div className={`input-group${emailError && emailTouched ? ' input-error' : ''}`}>
                                <input className='email-input' type='text' name='email' id='email' value={email} onChange={handleEmailChange} onBlur={handleEmailBlur} placeholder=' '/>
                                <label htmlFor='email'>Email</label>
                                {emailError && emailTouched && <span className='field-error'>{emailError}</span>}
                            </div>
                        </div>
                    </div>
                    <div className='ratings-section'>
                            <span className='header'>RATE YOUR SKILL LEVEL</span>
                            <section>
                                <RatingItem
                                  label="Algebra"
                                  ratingClass={algebraClass}
                                  onClick={index => handleRatingClick(index, setAlgebraClass, setAlgebraRating, setAlgebraRatingDescription)}
                                  onHover={index => handleRatingHover(index, setAlgebraRatingDescription)}
                                  onLeave={() => handleRatingLeave(algebraRating, setAlgebraRatingDescription)}
                                  description={algebraRatingDescription}
                                />
                                <RatingItem
                                  label="Advanced Math"
                                  ratingClass={advancedMathClass}
                                  onClick={index => handleRatingClick(index, setAdvancedMathClass, setAdvancedMathRating, setAdvancedMathRatingDescription)}
                                  onHover={index => handleRatingHover(index, setAdvancedMathRatingDescription)}
                                  onLeave={() => handleRatingLeave(advancedMathRating, setAdvancedMathRatingDescription)}
                                  description={advancedMathRatingDescription}
                                />
                            </section>
                            <section>
                                <RatingItem
                                  label="Problem Solving & Data Analysis"
                                  ratingClass={problemSolvingAndDataAnalysisClass}
                                  onClick={index => handleRatingClick(index, setProblemSolvingAndDataAnalysisClass, setProblemSolvingAndDataAnalysisRating, setProblemSolvingAndDataAnalysisRatingDescription)}
                                  onHover={index => handleRatingHover(index, setProblemSolvingAndDataAnalysisRatingDescription)}
                                  onLeave={() => handleRatingLeave(problemSolvingAndDataAnalysisRating, setProblemSolvingAndDataAnalysisRatingDescription)}
                                  description={problemSolvingAndDataAnalysisRatingDescription}
                                />
                                <RatingItem
                                  label="Geometry & Trigonometry"
                                  ratingClass={geometryAndTrigonometryClass}
                                  onClick={index => handleRatingClick(index, setGeometryAndTrigonometryClass, setGeometryAndTrigonometryRating, setGeometryAndTrigonometryRatingDescription)}
                                  onHover={index => handleRatingHover(index, setGeometryAndTrigonometryRatingDescription)}
                                  onLeave={() => handleRatingLeave(geometryAndTrigonometryRating, setGeometryAndTrigonometryRatingDescription)}
                                  description={geometryAndTrigonometryRatingDescription}
                                />
                            </section>
                            {signupError && <div className='error'>{errorMessage}</div>}
                    </div>
                    <div className='actions-section'>
                        <button type="submit" disabled={loading}>
                            {loading ? <div className='signup-spinner' /> : 'Create'}
                        </button>
                        <span className='login-link'>Already have an account? <Link to="/login">Log in</Link></span>
                    </div>
                </form>
            )}
        </div>
    );
}

export default Signup;